import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    FlexBox,
    FluidImage,
    SelectionSet,
    SelectionSetInput,
    SelectionSetLabel,
    SelectionSetTileContents,
    SelectionSetTileDescription,
    SelectionSetTileImageContainer,
    SelectionSetTileName,
    Typography,
} from '@vp/swan';
import { HelpMeAnswerButton } from './HelpMeAnswerButton';
import { ScrollableBox } from './ScrollableBox';
import type { StyleOption } from '../types/selectors';

export type { StyleOption };

export interface StyleTilesSelectorProps {
    options: StyleOption[];
    defaultValue?: string;
    onComplete: (option: StyleOption) => void;
}

export function StyleTilesSelector({ options, defaultValue, onComplete }: StyleTilesSelectorProps) {
    const [selectedValue, setSelectedValue] = useState<string | undefined>(defaultValue);
    const headingRef = useRef<HTMLElement>(null);

    useEffect(() => {
        requestAnimationFrame(() => {
            headingRef.current?.focus();
        });
    }, []);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedValue(event.target.value);
    };

    const handleContinue = () => {
        const option = options.find((o) => o.value === selectedValue);
        if (option) onComplete(option);
    };

    return (
        <Box
            style={{
                backgroundColor: 'var(--swan-sem-color-bg-active)',
                border: '1px solid var(--swan-sem-color-border-standard)',
                padding:
                    'var(--swan-sem-space-6) var(--swan-sem-space-6) var(--swan-sem-space-3) var(--swan-sem-space-6)',
                borderRadius:
                    'var(--swan-sem-border-radius-standard) var(--swan-sem-border-radius-standard) 0 0',
            }}
        >
            <Typography
                ref={headingRef}
                tabIndex={-1}
                fontSkin="title-item"
                style={{
                    // TODO: Use var(--swan-sem-space-below-heading) token once we figure out why it doesn't work
                    paddingBottom: 'var(--swan-sem-space-3)',
                    outline: 'none',
                    boxShadow: 'none',
                }}
            >
                Which designs do you like?{' '}
                <Typography as="span" textColor="subtle" fontSkin="body-standard">
                    (Please select 1)
                </Typography>
            </Typography>

            <ScrollableBox style={{ maxHeight: '320px' }}>
                <SelectionSet
                    skin="tiles-mini"
                    variant="single-select"
                    selectedValue={selectedValue}
                    onChange={handleChange}
                    aria-label="Which designs do you like?"
                >
                    {options.map((option) => (
                        <SelectionSetInput key={option.value} value={option.value}>
                            <SelectionSetLabel>
                                <SelectionSetTileImageContainer
                                    style={{
                                        width: '120px',
                                        height: '90px',
                                        minWidth: '120px',
                                        flexShrink: 0,
                                    }}
                                >
                                    <FluidImage
                                        src={option.imageUrl}
                                        alt={option.name}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </SelectionSetTileImageContainer>
                                <SelectionSetTileContents>
                                    <SelectionSetTileName>{option.name}</SelectionSetTileName>
                                    <SelectionSetTileDescription>
                                        {option.description}
                                    </SelectionSetTileDescription>
                                </SelectionSetTileContents>
                            </SelectionSetLabel>
                        </SelectionSetInput>
                    ))}
                </SelectionSet>
            </ScrollableBox>

            <FlexBox justifyContent="space-between" alignItems="center" gap={4}>
                <HelpMeAnswerButton
                    selectorType="style-tiles"
                    options={options.map(({ name }) => ({ name }))}
                />
                <Button skin="secondary" onClick={handleContinue} disabled={!selectedValue}>
                    Continue
                </Button>
            </FlexBox>
        </Box>
    );
}
